import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import './Customer.css';

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const { profile, updateProfile } = useAuth();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        phone: profile.phone || '',
        address: profile.address || '',
        city: profile.city || '',
        postal_code: profile.postal_code || '',
      });
    }
  }, [profile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setSuccess(false);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await updateProfile(formData);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Profile update error:', err);
      setError(t('customer.update_error') || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (!profile) {
    return <div className="customer-loading">Loading...</div>;
  }

  return (
    <div className="customer-container">
      <div className="customer-content">
        <h1 className="customer-title">{t('customer.profile') || 'প্রোফাইল'}</h1>

        <div className="profile-info-card">
          <h3>{t('customer.account_info') || 'অ্যাকাউন্ট তথ্য'}</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">{t('auth.username') || 'ইউজারনেম'}:</span>
              <span className="info-value">{profile.username}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('auth.email') || 'ইমেইল'}:</span>
              <span className="info-value">{profile.email}</span>
            </div>
          </div>
        </div>

        <div className="profile-form-card">
          <h3>{t('customer.edit_profile') || 'প্রোফাইল সম্পাদনা'}</h3>

          {success && (
            <div className="success-message">
              {t('customer.update_success') || 'Profile updated successfully!'}
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="profile-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="first_name">
                  {t('auth.first_name') || 'নাম'} *
                </label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="last_name">
                  {t('auth.last_name') || 'পদবি'} *
                </label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="phone">
                {t('auth.phone') || 'ফোন নম্বর'} *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="address">
                {t('auth.address') || 'ঠিকানা'} *
              </label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                rows={3}
                disabled={loading}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="city">
                  {t('auth.city') || 'শহর'} *
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="postal_code">
                  {t('auth.postal_code') || 'পোস্টাল কোড'} *
                </label>
                <input
                  type="text"
                  id="postal_code"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <button 
              type="submit" 
              className="submit-button"
              disabled={loading}
            >
              {loading ? (t('customer.updating') || 'আপডেট হচ্ছে...') : (t('customer.update_profile') || 'প্রোফাইল আপডেট করুন')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
